import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import logger from "@/lib/logger";

type InviteRow = {
  id: string;
  label: string | null;
  expiresAt: string;
  status: "pending" | "redeemed" | "expired" | "revoked";
};

type UserRow = {
  id: string;
  email: string;
  name: string | null;
  entitled: boolean;
};

const DEFAULT_EXPIRY_DAYS = 14;

async function fetchJson<T>(url: string): Promise<T> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`${url} responded ${response.status}`);
  }
  return (await response.json()) as T;
}

/**
 * Minimal mobile-first admin surface (spec §10.3): mint/list/revoke invites,
 * grant/revoke entitlements. Route access is server-verified by AdminGuard
 * (entitlement === "ADMIN" from /api/auth/me) and every call is re-verified
 * kernel-side (isAdmin tool gates) — this UI cannot escalate.
 */
export default function AdminPage() {
  const [invites, setInvites] = useState<InviteRow[]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [label, setLabel] = useState("");
  const [expiresInDays, setExpiresInDays] = useState<number | "">(
    DEFAULT_EXPIRY_DAYS
  );
  const [mintedLink, setMintedLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [inviteRows, userRows] = await Promise.all([
        fetchJson<InviteRow[]>("/api/admin/invites"),
        fetchJson<UserRow[]>("/api/admin/users"),
      ]);
      setInvites(inviteRows);
      setUsers(userRows);
      setError(null);
    } catch (err) {
      logger.error({ err }, "[Admin] Failed to load data");
      setError("Failed to load admin data.");
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const mintInvite = async () => {
    try {
      const response = await fetch("/api/admin/invites", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          label: label || undefined,
          expiresInDays: expiresInDays || DEFAULT_EXPIRY_DAYS,
        }),
      });
      if (!response.ok) {
        throw new Error(`mint responded ${response.status}`);
      }
      const { token } = (await response.json()) as { token: string };
      // Spec §10.2: link built client-side — correct for whatever origin
      // serves the SPA; no PUBLIC_APP_URL env needed.
      setMintedLink(`${window.location.origin}/invite/${token}`);
      setCopied(false);
      setLabel("");
      await refresh();
    } catch (err) {
      logger.error({ err }, "[Admin] Failed to mint invite");
      setError("Failed to create invite.");
    }
  };

  const revokeInvite = async (id: string) => {
    try {
      const response = await fetch(`/api/admin/invites/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        throw new Error(`revoke responded ${response.status}`);
      }
      setError(null);
      await refresh();
    } catch (err) {
      logger.error({ err }, "[Admin] Failed to revoke invite");
      setError("Failed to revoke invite.");
    }
  };

  const setEntitlement = async (userId: string, grant: boolean) => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/entitlement`, {
        method: grant ? "POST" : "DELETE",
      });
      if (!response.ok) {
        throw new Error(`entitlement update responded ${response.status}`);
      }
      setError(null);
      await refresh();
    } catch (err) {
      logger.error({ err }, "[Admin] Failed to update user entitlement");
      setError("Failed to update user entitlement.");
    }
  };

  const copyLink = async () => {
    if (mintedLink) {
      await navigator.clipboard.writeText(mintedLink);
      setCopied(true);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-8 p-4 pb-16">
      <h2 className="font-semibold text-2xl">Admin</h2>
      {error && <p className="text-destructive text-sm">{error}</p>}

      <section className="flex flex-col gap-3">
        <h3 className="font-semibold text-lg">Mint invite</h3>
        <Input
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Label (e.g. Dave)"
          value={label}
        />
        <Input
          min={1}
          onChange={(e) => {
            const raw = e.target.value;
            setExpiresInDays(raw === "" ? "" : Number(raw));
          }}
          type="number"
          value={expiresInDays}
        />
        <Button onClick={mintInvite} type="button">
          Create invite
        </Button>
      </section>

      <section className="flex flex-col gap-3">
        <h3 className="font-semibold text-lg">Invites</h3>
        {invites.length === 0 && (
          <p className="text-muted-foreground text-sm">No invites yet.</p>
        )}
        {invites.map((invite) => (
          <div
            className="flex items-center justify-between gap-2 rounded-lg border p-3"
            key={invite.id}
          >
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-medium text-sm">
                {invite.label ?? "(no label)"}
              </span>
              <span className="text-muted-foreground text-xs">
                expires {new Date(invite.expiresAt).toLocaleDateString()}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">{invite.status}</Badge>
              {invite.status === "pending" && (
                <Button
                  onClick={() => revokeInvite(invite.id)}
                  size="sm"
                  type="button"
                  variant="destructive"
                >
                  Revoke
                </Button>
              )}
            </div>
          </div>
        ))}
      </section>

      <section className="flex flex-col gap-3">
        <h3 className="font-semibold text-lg">Users</h3>
        {users.map((user) => (
          <div
            className="flex items-center justify-between gap-2 rounded-lg border p-3"
            key={user.id}
          >
            <div className="flex min-w-0 flex-col">
              {user.name ? (
                <>
                  <span className="truncate font-medium text-sm">
                    {user.name}
                  </span>
                  <span className="truncate text-muted-foreground text-xs">
                    {user.email}
                  </span>
                </>
              ) : (
                <span className="truncate font-medium text-sm">
                  {user.email}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={user.entitled ? "default" : "outline"}>
                {user.entitled ? "entitled" : "no access"}
              </Badge>
              <Button
                onClick={() => setEntitlement(user.id, !user.entitled)}
                size="sm"
                type="button"
                variant={user.entitled ? "destructive" : "default"}
              >
                {user.entitled ? "Revoke" : "Grant"}
              </Button>
            </div>
          </div>
        ))}
      </section>

      <Dialog
        onOpenChange={(open) => {
          if (!open) {
            setMintedLink(null);
          }
        }}
        open={mintedLink !== null}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite link</DialogTitle>
            <DialogDescription>
              This link is shown ONCE — copy it now and send it over chat.
            </DialogDescription>
          </DialogHeader>
          <p className="break-all rounded-md bg-muted p-3 font-mono text-xs">
            {mintedLink}
          </p>
          <Button onClick={copyLink} type="button">
            {copied ? "Copied" : "Copy link"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
